import { inject, Injectable, NgZone } from '@angular/core'

import { Observable, Subject } from 'rxjs'

import { ChatEventType } from '@yoshu-lib/models/chat-event.model'

import { isJSONString } from '@yoshu-lib/utils/is-json-string'

import { ENVIRONMENT } from '../tokens/environment.token'

interface WebSocketMessage {
  type: string
  data?: unknown
}

@Injectable({ providedIn: 'root' })
export class SocketService {
  private readonly env = inject(ENVIRONMENT)

  private ws?: WebSocket
  private reconnectAttempts = 0
  private maxReconnectAttempts = 5
  private reconnectDelay = 1000

  private ngZone = inject(NgZone)

  private eventSubjects = new Map<string, Subject<unknown>>()

  get connected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN
  }

  init(token: string) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      return
    }

    const url = `${this.env.socketUrl}/ws/chat/?token=${token}`
    this.ws = new WebSocket(url)

    this.ws.onopen = () => {
      console.log('WebSocket connected')
      this.reconnectAttempts = 0
    }

    this.ws.onmessage = (event) => {
      try {
        const message: WebSocketMessage = JSON.parse(event.data)
        const subject = this.eventSubjects.get(message.type)
        if (subject) {
          this.ngZone.run(() => subject.next(message.data))
        }
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error)
      }
    }

    this.ws.onclose = () => {
      console.log('WebSocket disconnected')
      this.attemptReconnect(token)
    }

    this.ws.onerror = (error) => {
      console.error('WebSocket error:', error)
    }
  }

  private attemptReconnect(token: string) {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++
      setTimeout(() => this.init(token), this.reconnectDelay * this.reconnectAttempts)
    }
  }

  listen<T>(event: string): Observable<T> {
    if (!this.eventSubjects.has(event)) {
      this.eventSubjects.set(event, new Subject<unknown>())
    }
    return this.eventSubjects.get(event)!.asObservable() as Observable<T>
  }

  emit(event: string, data?: unknown) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket not connected')
    }
    const message: WebSocketMessage = { type: event, data }
    this.ws.send(JSON.stringify(message))
  }

  disconnect() {
    this.ws?.close()
    this.eventSubjects.clear()
  }
}
